import { NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, OnInit, ChangeDetectorRef, input, output, inject, effect, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Roles, StaffStatus } from '@fitness-force/enum';
import type { CustomAutomationData, STAFFINTERFACE } from '@fitness-force/models';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { STAFF_STATE } from '@fitness-force/state';
import { ToastMessageService } from '@fitness-force/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';

import { MultiSelectModule } from 'primeng/multiselect';
import { StyleClassModule } from 'primeng/styleclass';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'staff-notification-setting-popup',
  templateUrl: './staff-notification-setting-popup.component.html',
  styleUrls: ['./staff-notification-setting-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    CheckboxModule,
    DialogModule,
    ButtonModule,
    StyleClassModule,
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    AvatarModule,
    ShortNamePipe,
    TranslateModule,
    NgClass,
    TitleCasePipe
]
})
export class StaffNotificationSettingPopupComponent implements OnInit {
  private readonly cd = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);

  readonly showPopup = input(false, { alias: "visible" });
  readonly firstTimeSelected = input(false);
  readonly automationData = input<any>(undefined, { alias: "Data" });
  readonly HidePopup = output<{
    closePopup: boolean;
    data: CustomAutomationData;
    showAnotherPopup: boolean;
}>({ alias: 'hide' });

  modelData: CustomAutomationData = {} as CustomAutomationData;
  associatedStaffCheck: boolean = false;
  otherStaffCheck: boolean = false;
  accessedStaffs: STAFFINTERFACE[] = [];
  selectedStaff: any;
  staffNotificationForm: UntypedFormGroup = new UntypedFormGroup({});

  showDialog = signal(false);

  constructor() {
    this.staffNotificationForm = this.fb.group({
      notify_record_associated_staff:[false, Validators.required],
      notify_record_other_staff:[false],
      notify_additional_staff_to:[]
    });

    effect(() => {
      this.showDialog.set(this.showPopup());
    });
  }

  ngOnInit(): void {
    this.store.select(STAFF_STATE).subscribe((staffs: any) => {
      this.accessedStaffs = staffs.staffList.filter(
        (s: STAFFINTERFACE) =>
          s.role !== Roles.FFAdmin &&
          s.role.toString() !== Roles[Roles.FFAdmin] &&
          (s.status === StaffStatus.Active ||
            s.status.toString() === StaffStatus[StaffStatus.Active])
      );
    });

    const automationData = this.automationData();
    if (automationData) {
      this.modelData = automationData as CustomAutomationData;
      if (this.modelData.notificationSelected) {
        this.associatedStaffCheck = this.modelData.associatedStaff;
        if (this.modelData.additionalStaffTo.length > 0)
        {
          this.otherStaffCheck = true;
          this.selectedStaff = this.accessedStaffs.filter(o => this.modelData.additionalStaffTo.includes(o.id));
        }
        this.staffNotificationForm = this.modelData.staffNotificationForm;
      }
      this.cd.detectChanges();
    }

     this.SelectedOtherStaffFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: boolean) => {
        if(!value)
        {
          this.SelectedAdditionalStaffFormControl.setValue([]);
          this.SelectedAdditionalStaffFormControl.clearValidators();
        }
        else{
          this.SelectedAdditionalStaffFormControl.setValidators(Validators.required);
        }
      });
  }

  SelectNotificationSetting() {
    this.modelData.notificationSelected = true;
    this.modelData.associatedStaff = this.SelectedAssociatedStaffFormControl && this.SelectedAssociatedStaffFormControl.value ? this.SelectedAssociatedStaffFormControl.value  : false ;
    this.modelData.staffNotificationForm = this.staffNotificationForm;
    if (this.SelectedOtherStaffFormControl && this.SelectedOtherStaffFormControl.value) {
      this.modelData.additionalStaffTo = this.SelectedAdditionalStaffFormControl && this.SelectedAdditionalStaffFormControl.value ? this.SelectedAdditionalStaffFormControl.value.map((staff: any) => staff.id) : [];
    } 
    else{
      this.modelData.additionalStaffTo =[];
    }
    this.HidePopup.emit({
      closePopup: true,
      data: this.modelData,
      showAnotherPopup: this.firstTimeSelected(),
    });
    //this.ref.close(this.modelData);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showPopup()) {
      this.closePopup();
    }
  }

  closePopup(): void {
    this.showDialog.set(false);
    this.HidePopup.emit({
      closePopup: true,
      data: this.modelData,
      showAnotherPopup: false,
    });
    this.cd.detectChanges();
  }

  get SelectedAdditionalStaffFormControl()
  {
    return this.staffNotificationForm.controls['notify_additional_staff_to'] as UntypedFormControl;
  }

  get SelectedAssociatedStaffFormControl()
  {
    return this.staffNotificationForm.controls['notify_record_associated_staff'] as UntypedFormControl;
  }

  get SelectedOtherStaffFormControl()
  {
    return this.staffNotificationForm.controls['notify_record_other_staff'] as UntypedFormControl;
  }
}